import React, { useState } from "react";
import "./style.scss";
import { useSelector } from "react-redux";
import CtaComponent from "../../../components/reusable-ui/cta";
import { CTA_SIZE, CTA_TYPE, USER_ROLES } from "../../../data/constants";
import BranchFilterComponent from "../../../components/reusable-ui/branch-filter";
import SearchInputComponent from "../../../components/reusable-ui/search-input";
import { allowEverything } from "../../../utils";
import { cloneDeep, pick } from "lodash";
import { OUTLET_BULK_ACTIONS } from "../../../data/outlet-status-constants";
import { isAndroid, isMobileDevice } from "../../../helpers/device-info-helper";
import { Menu, MenuItem } from "@mui/material";
import { RootState } from "../../../redux/store";
import { isSourcePos } from "../../../helpers/common-helper";
import { isAdminUser, isUser } from "../../../helpers/user-info-helpers";

interface Props {
  setSelectedBranches: any;
  setSearchData: any;
  bulkActionConfig: any;
  setBulkActionConfig: any;
  outletGroupByBranchMap: any;
  addAllOutletsForBranch: any;
  setIsAddNewConfigModalOpen?: any;
  getBranchOutletsCount: any;
  getSearchResults: any;
  searchData: any;
  flatBranchOutletsGroup: any;
  selectedBranches: any;
}
const OutletActionHeader = (props: Props) => {
  const {
    setSelectedBranches,
    setSearchData,
    bulkActionConfig,
    setBulkActionConfig,
    outletGroupByBranchMap,
    addAllOutletsForBranch,
    setIsAddNewConfigModalOpen,
    getBranchOutletsCount,
    getSearchResults,
    searchData,
    flatBranchOutletsGroup,
    selectedBranches,
  } = props;
  const [bulkActionMenuAnchorEl, setBulkActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const { outletsData, permittedBranchesData } = useSelector(
    (state: RootState) => ({
      outletsData: state.outletStatusReducer.outletsData,
      permittedBranchesData: state.businessReducer.userData.permittedBranches,
    })
  );
  const openBulkActionMenu = Boolean(bulkActionMenuAnchorEl);
  const handleBulkActionMenuCtaClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setBulkActionMenuAnchorEl(event.currentTarget);
  };
  const handleBulkActionMenuClose = () => {
    setBulkActionMenuAnchorEl(null);
  };

  const onMenuOptionClick = (e: any, type: string) => {
    e?.stopPropagation();
    setBulkActionConfig({
      active: true,
      type,
    });
    handleBulkActionMenuClose();
  };

  const onBulkActionCtaClick = () => {
    setBulkActionConfig((prevVal: any) => ({
      ...prevVal,
      showConfirmationModal: true,
    }));
  };

  const onClearAllCtaClick = () => {
    setBulkActionConfig((prevVal: any) => ({
      ...prevVal,
      selectedOutletsMap: {},
    }));
  };

  const onSelectAllCtaClick = () => {
    let branchIdList = outletGroupByBranchMap
      ? Object.keys(outletGroupByBranchMap)
      : [];
    let dataToUpdate = branchIdList.reduce((ac: any, branchId: string) => {
      return addAllOutletsForBranch(branchId, ac);
    }, {});
    setBulkActionConfig((prevVal: any) => ({
      ...prevVal,
      selectedOutletsMap: dataToUpdate,
    }));
  };

  function renderBulkActionConfirmCta() {
    let selectedOutletsMap = cloneDeep(
      bulkActionConfig?.selectedOutletsMap || {}
    );
    let selectedOutletsCount = getBranchOutletsCount(selectedOutletsMap);
    return (
      <>
        <CtaComponent
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.large}
          ctaText={"Cancel"}
          ctaType={CTA_TYPE.secondary_cta}
          onCtaClick={() => setBulkActionConfig({ active: false })}
          customClass={`text-16 ml-[24px] h-[40px] leading-none py-[12px] sm:w-[calc(50%-6px)] sm:ml-0
            sm:mr-[6px] sm:text-[14px] sm:whitespace-nowrap sm:h-[44px]`}
        ></CtaComponent>
        <CtaComponent
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.large}
          ctaText={
            bulkActionConfig.type === OUTLET_BULK_ACTIONS.bulk_menu_sync.type
              ? "Sync Menu"
              : "Change Outlet Status"
          }
          ctaType={CTA_TYPE.active_cta}
          onCtaClick={onBulkActionCtaClick}
          customClass={`text-16 ml-[12px] h-[40px] leading-none py-[12px] sm:w-[calc(50%-6px)] sm:ml-[6px]
            sm:text-[14px] sm:whitespace-nowrap sm:h-[44px]`}
          isDisabled={selectedOutletsCount === 0}
        ></CtaComponent>
      </>
    );
  }
  function renderBulkActionCta() {
    return bulkActionConfig?.active ? (
      renderBulkActionConfirmCta()
    ) : (
      <>
        {isAdminUser() && (
          <CtaComponent
            ctaSize={CTA_SIZE.large}
            ctaText={OUTLET_BULK_ACTIONS.bulk_menu_sync.label}
            ctaType={CTA_TYPE.secondary_cta}
            onCtaClick={() =>
              setBulkActionConfig({
                active: true,
                type: OUTLET_BULK_ACTIONS.bulk_menu_sync.type,
              })
            }
            customClass={"text-16 ml-[12px] h-[40px] py-[12px] leading-none"}
          ></CtaComponent>
        )}
        <CtaComponent
          ctaSize={CTA_SIZE.large}
          ctaText={OUTLET_BULK_ACTIONS.bulk_outlet_status_update.label}
          ctaType={CTA_TYPE.secondary_cta}
          onCtaClick={() =>
            setBulkActionConfig({
              active: true,
              type: OUTLET_BULK_ACTIONS.bulk_outlet_status_update.type,
            })
          }
          customClass={"text-16 ml-[12px] h-[40px] py-[12px] leading-none"}
        ></CtaComponent>
      </>
    );
  }

  function renderBranchFilterDropdown(config: {
    parentClass?: string;
    customClass?: string;
  }) {
    const { parentClass = "", customClass = "" } = config;
    return (
      <BranchFilterComponent
        onApplyCtaClick={(branchIds: any) => setSelectedBranches(branchIds)}
        selectAllBranches={true}
        parentClass={`w-[220px] ml-0 ${parentClass || ""}`}
        customClass={customClass}
        branchDataList={permittedBranchesData}
      ></BranchFilterComponent>
    );
  }

  function renderSearchInputComponent(config: {
    customClass?: string;
    elementWidthClass?: string;
  }) {
    let { customClass = "", elementWidthClass = "" } = config;
    return (
      <SearchInputComponent
        placeholder="Search by Branch Name/ Code, Channel Name, Outlet id"
        customClass={`text-15 py-[11px] h-[40px] leading-none ${
          customClass || ""
        }`}
        onSerachInput={(value: string) =>
          setSearchData((prev: any) => ({ ...prev, value }))
        }
        regexCheck={allowEverything}
        elementWidthClass={elementWidthClass}
      ></SearchInputComponent>
    );
  }

  function renderActionHeaderDesktop() {
    return (
      <div
        className={`flex items-center justify-between bg-white p-[20px] mb-[8px]
        fixed top-[85px] z-[2] border-y border-[#F0F0F0]
        ${isSourcePos() ? "w-[100%]" : "w-[calc(100%-96px)]"}
      `}
      >
        {!isUser() &&
          renderBranchFilterDropdown({
            customClass: "!pl-[24px] !h-[40px]",
            parentClass: "!h-[40px]",
          })}
        <div className="flex items-center justify-end ml-[36px] w-[calc(100%-226px)]">
          {renderSearchInputComponent({
            elementWidthClass: `${
              bulkActionConfig?.active
                ? "w-[calc(100%-348px)]"
                : "w-[calc(100%-486px)]"
            } max-w-[440px] leading-none`,
          })}
          {bulkActionConfig?.active
            ? null
            : isAdminUser() && (
                <CtaComponent
                  ctaSize={CTA_SIZE.large}
                  ctaText="+ Add"
                  ctaType={CTA_TYPE.secondary_cta}
                  onCtaClick={() => setIsAddNewConfigModalOpen(true)}
                  customClass={`text-16 ml-[20px] h-[40px] text-[#0074FB] border-[#0074FB] px-[10px] py-[12px]
                leading-none whitespace-nowrap`}
                ></CtaComponent>
              )}

          {renderBulkActionCta()}
        </div>
      </div>
    );
  }

  function renderBulkActionMenu() {
    return (
      <>
        <button
          id="bulk-action-menu-button"
          aria-controls={openBulkActionMenu ? "bulk-action-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openBulkActionMenu ? "true" : undefined}
          onClick={handleBulkActionMenuCtaClick}
          className={`bulk-action-menu-button flex items-center py-[6px] pr-[8px] pl-[16px] rounded-[8px]
            border border-[#EBEBEB]
          `}
        >
          <p className="text-16 text-black leading-none sm:text-[14px]">
            {OUTLET_BULK_ACTIONS[bulkActionConfig?.type]?.label ||
              "Bulk Action"}
          </p>
          <div
            className={`rista-sprite ${
              openBulkActionMenu
                ? "ic-dropdown-arrow-up"
                : "ic-dropdown-arrow-down"
            }`}
          ></div>
        </button>
        <Menu
          id="bulk-action-menu"
          anchorEl={bulkActionMenuAnchorEl}
          open={openBulkActionMenu}
          onClose={handleBulkActionMenuClose}
          MenuListProps={{
            "aria-labelledby": "bulk-action-menu-button",
          }}
          PaperProps={{
            style: {
              width: "200px",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {Object.keys(OUTLET_BULK_ACTIONS).map((type: string) => {
            return (
              <MenuItem
                onClick={(e) => onMenuOptionClick(e, type)}
                key={`key-${type}`}
                className="sm:text-[14px]"
              >
                {OUTLET_BULK_ACTIONS[type]?.label}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  function renderMobilePageHeader() {
    return (
      <div
        className={`mobile-page-header flex items-center justify-between fixed ${isAndroid() ? 'top-[0px]' : 'top-[54px]'} w-full
            bg-white h-[74px] px-[16px] border-b border-[#f0f0f0] z-[2]
        `}
      >
        <h1 className="text-24 font-semibold sm:text-[18px]">Outlet Status</h1>

        {bulkActionConfig?.active ? null : (
          <div className="flex items-center">
            <CtaComponent
              ctaSize={CTA_SIZE.small}
              ctaText="+"
              ctaType={CTA_TYPE.secondary_cta}
              onCtaClick={() => setIsAddNewConfigModalOpen(true)}
              customClass={`text-[28px] text-[#0074FB] h-[34px] w-[34px] font-medium mr-[10px] border-[#0074FB]
                leading-none whitespace-nowrap`}
            ></CtaComponent>
            {renderBulkActionMenu()}
          </div>
        )}
      </div>
    );
  }

  function renderActionHeaderMobile() {
    return (
      <div
        className={`w-full fixed ${isAndroid() ? 'top-[70px]' : 'top-[128px]'} bg-white px-[16px] py-[12px] z-[2] flex items-center justify-between
          border-b border-[#F0F0F0]
        `}
      >
        {renderSearchInputComponent({
          elementWidthClass: "w-[calc(100%-190px)] leading-none",
        })}
        {renderBranchFilterDropdown({
          parentClass: "!sm:mt-0 !sm:w-[180px]",
          customClass: "sm:text-[14px]",
        })}
      </div>
    );
  }

  function renderSelectedOutletsInfoBar() {
    let selectedOutletsMap = cloneDeep(
        bulkActionConfig?.selectedOutletsMap || {}
      ),
      branchFilteredOutlets = pick(selectedOutletsMap, selectedBranches),
      selectedOutletsList = flatBranchOutletsGroup(branchFilteredOutlets) || [],
      searchSelectedOutletList = getSearchResults(
        searchData?.value,
        selectedOutletsList
      );
    let selectedOutletsCount = searchSelectedOutletList?.length || 0;
    let allOutletsCount = getBranchOutletsCount(outletGroupByBranchMap);
    return (
      <div
        className={`flex items-center justify-between bg-white p-[20px] mb-[8px]
          fixed top-[166px] w-[calc(100%-96px)] z-1 sm:w-full sm:items-start sm:top-[192px]
          sm:px-[16px] sm:py-[12px]
        `}
      >
        <div className="flex items-center sm:flex-col sm:items-start">
          <p className="text-17 font-semibold sm:mb-[10px]">
            {OUTLET_BULK_ACTIONS[bulkActionConfig?.type]?.label}
          </p>
          <p className="text-14 text-black/70 ml-[12px] sm:ml-0">
            {isMobileDevice()
              ? "Click & Select the outlet card"
              : OUTLET_BULK_ACTIONS[bulkActionConfig?.type]?.actionBarText}
          </p>
        </div>
        <div className="flex items-center sm:flex-col sm:items-end">
          <div className="flex items-center sm:mb-[10px]">
            {selectedOutletsCount &&
            selectedOutletsCount !== allOutletsCount ? (
              <p className="text-16 font-medium sm:text-[12px] sm:font-normal">{`${selectedOutletsCount} outlets selected`}</p>
            ) : null}

            {selectedOutletsCount === allOutletsCount ? (
              <p className="text-16 font-medium ml-[20px] mr-[14px] sm:mr-0 sm:text-[12px] sm:font-normal">
                All Outlets Selected
              </p>
            ) : null}

            {selectedOutletsCount === allOutletsCount ? null : (
              <div
                className={`text-16 text-[#0074FB] font-medium cursor-pointer
                ml-[20px] mr-[14px] sm:mr-0 sm:ml-[8px] sm:text-[12px]
              `}
                onClick={onSelectAllCtaClick}
              >
                Select All
              </div>
            )}
          </div>

          {selectedOutletsCount ? (
            <div
              className={`text-16 text-[#0074FB] font-medium cursor-pointer
                pl-[14px] border-l border-[#DDDDDD] sm:border-none sm:text-[12px]
              `}
              onClick={onClearAllCtaClick}
            >
              Clear All
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return isMobileDevice() ? (
    <>
      {renderMobilePageHeader()}
      {renderActionHeaderMobile()}
      {bulkActionConfig?.active ? (
        <>
          {renderSelectedOutletsInfoBar()}
          <div
            className={`fixed bottom-0 w-full bg-white px-[16px] flex items-center z-1
              justify-between py-[16px]
            `}
          >
            {renderBulkActionConfirmCta()}
          </div>
        </>
      ) : null}
    </>
  ) : (
    <>
      {renderActionHeaderDesktop()}
      {bulkActionConfig?.active ? renderSelectedOutletsInfoBar() : null}
    </>
  );
};

export default OutletActionHeader;
